import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { clx } from "../../../utils/Helpers";
import "./UIReadMoreText.scss";

/** Text cut at `lines` lines, with a Read more / Read less toggle once it runs longer. */
const UIReadMoreText = ({
  children,
  lines,
  readMoreLabel,
  readLessLabel,
  className,
  textClassName,
}) => {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Measured while cut, and again on resize: the width decides where the lines break.
  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node || expanded) return undefined;
    const measure = () => setOverflows(node.scrollHeight > node.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children, lines, expanded]);

  return (
    <div className={clx("ui-read-more", className)}>
      <span
        ref={textRef}
        className={clx(
          "ui-read-more__text",
          !expanded && "ui-read-more__text--clamped",
          textClassName,
        )}
        style={{ "--ui-read-more-lines": lines }}
      >
        {children}
      </span>
      {overflows && (
        <button
          type="button"
          className="text-label-sm ui-read-more__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      )}
    </div>
  );
};

UIReadMoreText.propTypes = {
  children: PropTypes.node,
  /** Lines shown before Read more. */
  lines: PropTypes.number,
  readMoreLabel: PropTypes.string.isRequired,
  readLessLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
  /** Classes of the text itself, e.g. the typography of the field it sits in. */
  textClassName: PropTypes.string,
};

UIReadMoreText.defaultProps = {
  children: null,
  lines: 2,
  className: undefined,
  textClassName: undefined,
};

export default UIReadMoreText;
